// src/components/common/Sidebar.jsx - Updated with Notifications
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  Users,
  BarChart2,
  MessageSquare,
  Settings,
  Package,
  Tag,
  QrCode,
  FileText,
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  ShoppingBag,
  Store,
  Building2,
  CreditCard,
  Bell,
  TrendingUp,
  GitBranch,
  UserCircle,
  ShieldAlert,
  Megaphone,
} from 'lucide-react';
import { authApi } from '../../api/auth-api';
import notificationApi from '../../api/notification-api';
import { useQuery, useMutation } from '@apollo/client';
import { GET_TICKET_UNREAD_COUNT, GET_TICKETS_BY_USER, MARK_TICKET_READ } from '../../api/messages-api';
import { GET_PAYMENT_REQUESTS_STATISTICS } from '../../api/payment-requests-api';
import { GET_FRAUD_CASE_IDS } from '../../api/fraud-cases-api';
// Import logo images properly - adjust paths as needed
import logo from '../../assets/images/logo.png';
import logoM from '../../assets/images/logoM.png';

const isMobileWidth = () => window.innerWidth < 1024;

const Sidebar = ({ collapsed: propCollapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userType, setUserType] = useState('');
  const [userData, setUserData] = useState({});
  const [collapsed, setCollapsed] = useState(() => isMobileWidth() ? true : (propCollapsed ?? false));
  const [expandedGroups, setExpandedGroups] = useState({});
  // Prevents feedback loop: when propCollapsed sets our state, don't call onToggle back
  const fromPropRef = useRef(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [labUnreadMessageCount, setLabUnreadMessageCount] = useState(0);
  const [unreadFraudCount, setUnreadFraudCount] = useState(0);

  const computeFraudUnread = (ids) => {
    try {
      const readIds = new Set(JSON.parse(localStorage.getItem('fraud_read_ids') || '[]'));
      setUnreadFraudCount(ids.filter(fc => !readIds.has(fc.id)).length);
    } catch {
      setUnreadFraudCount(0);
    }
  };

  // Poll for unread ticket count (admin only)
  const { data: ticketStatsData } = useQuery(GET_TICKET_UNREAD_COUNT, {
    skip: authApi.getUserType() !== 'ADMIN',
    fetchPolicy: 'network-only',
    pollInterval: 30000
  });

  // Poll for pending payment requests count (admin only)
  const { data: paymentStatsData } = useQuery(GET_PAYMENT_REQUESTS_STATISTICS, {
    skip: authApi.getUserType() !== 'ADMIN',
    fetchPolicy: 'network-only',
    pollInterval: 30000
  });
  const pendingPaymentCount = paymentStatsData?.paymentRequestsStatistics?.pending ?? 0;

  // Poll for fraud cases (admin only) — compute unread vs localStorage
  const { data: fraudCaseIdsData } = useQuery(GET_FRAUD_CASE_IDS, {
    skip: authApi.getUserType() !== 'ADMIN',
    fetchPolicy: 'network-only',
    pollInterval: 30000
  });

  // Poll for unread messages count (laboratory only)
  const { data: labTicketsData, refetch: refetchLabTickets } = useQuery(GET_TICKETS_BY_USER, {
    variables: { userId: authApi.getUserData()?.id || '' },
    skip: authApi.getUserType() !== 'LABORATORY' || !authApi.getUserData()?.id,
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000
  });

  const [markTicketRead] = useMutation(MARK_TICKET_READ);

  // Recompute when query data changes or when a case is marked read in FraudCases page
  useEffect(() => {
    if (!fraudCaseIdsData?.fraudCases) return;
    computeFraudUnread(fraudCaseIdsData.fraudCases);
    const handler = () => computeFraudUnread(fraudCaseIdsData.fraudCases);
    window.addEventListener('fraud-read-updated', handler);
    return () => window.removeEventListener('fraud-read-updated', handler);
  }, [fraudCaseIdsData]);

  useEffect(() => {
    if (ticketStatsData?.ticketStats) {
      try {
        const stats = JSON.parse(ticketStatsData.ticketStats);
        setUnreadMessageCount(stats.unreadTickets || 0);
      } catch {}
    }
  }, [ticketStatsData]);

  useEffect(() => {
    if (labTicketsData?.ticketsByUser) {
      const count = labTicketsData.ticketsByUser.filter(t => t.hasUnreadMessages).length;
      setLabUnreadMessageCount(count);
    }
  }, [labTicketsData]);

  const handleLabMessagesClick = async () => {
    if (!labTicketsData?.ticketsByUser) return;
    const unread = labTicketsData.ticketsByUser.filter(t => t.hasUnreadMessages);
    if (unread.length === 0) return;
    setLabUnreadMessageCount(0);
    try {
      await Promise.all(unread.map(t => markTicketRead({ variables: { id: t.id } })));
      refetchLabTickets();
    } catch {}
  };
  
  useEffect(() => {
    // Sync with prop but mark it so the collapsed effect won't echo back via onToggle
    if (propCollapsed !== undefined && propCollapsed !== collapsed) {
      fromPropRef.current = true;
      setCollapsed(propCollapsed);
    }
  }, [propCollapsed]);

  useEffect(() => {
    const storedUserType = authApi.getUserType() || 'ADMIN';
    setUserType(storedUserType);
    setUserData(authApi.getUserData() || {});

    // On mobile, always start collapsed — ignore any desktop localStorage value
    if (!isMobileWidth()) {
      const storedExpandedState = localStorage.getItem('sidebar_expanded');
      if (storedExpandedState) {
        setCollapsed(storedExpandedState === 'false');
      }
    }

    loadUnreadNotificationCount();
  }, [location.pathname]);

  // Load unread notification count
  const loadUnreadNotificationCount = async () => {
    try {
      const userId = authApi.getUserData()?.id;
      if (userId) {
        const counts = await notificationApi.getNotificationCounts(userId);
        setUnreadNotificationCount(counts.unread || 0);
      }
    } catch (error) {
      console.error('Error loading notification counts:', error);
    }
  };

  // Refresh notification count when user navigates to notifications page
  useEffect(() => {
    if (location.pathname.includes('/notifications')) {
      loadUnreadNotificationCount();
    }
  }, [location.pathname]);

  // Set up periodic refresh of notification counts (every 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      loadUnreadNotificationCount();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);
  
  // Save collapsed state and notify parent — but skip when change came from propCollapsed
  useEffect(() => {
    if (fromPropRef.current) {
      fromPropRef.current = false;
      return;
    }
    if (!isMobileWidth()) {
      localStorage.setItem('sidebar_expanded', (!collapsed).toString());
    }
    if (onToggle) {
      onToggle(collapsed);
    }
  }, [collapsed, onToggle]);
  
  const isPreviewMode = !!sessionStorage.getItem('adminSession');
  const isLabUser = userType === 'LABORATORY';

  // Color scheme: blue for lab, green for admin
  const c = isLabUser ? {
    bg:           'bg-blue-800',
    border:       'border-blue-700',
    activeItem:   'bg-blue-700',
    hoverItem:    'hover:bg-blue-700',
    text:         'text-blue-200',
    subtext:      'text-blue-300',
    avatar:       'bg-blue-600',
    hoverAvatar:  'hover:bg-blue-500',
    toggleText:   'text-blue-800',
    toggleBorder: 'border-blue-200',
  } : {
    bg:           'bg-green-800',
    border:       'border-green-700',
    activeItem:   'bg-green-700',
    hoverItem:    'hover:bg-green-700',
    text:         'text-green-200',
    subtext:      'text-green-300',
    avatar:       'bg-green-600',
    hoverAvatar:  'hover:bg-green-500',
    toggleText:   'text-green-800',
    toggleBorder: 'border-green-200',
  };

  const handleExitPreview = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    localStorage.setItem('authToken', session.authToken);
    localStorage.setItem('userType', session.userType);
    localStorage.setItem('userData', session.userData);
    sessionStorage.removeItem('adminSession');
    navigate(`/admin/laboratories/${session.labId}`);
  };

  // Navigation items for different user types
  const navigationItems = {
    ADMIN: [
      {
        name: 'Dashboard',
        path: '/admin/dashboard',
        icon: <LayoutGrid size={collapsed ? 22 : 18} />
      },
      {
        name: 'Utilisateurs',
        path: '/admin/users',
        icon: <Users size={collapsed ? 22 : 18} />
      },
      {
        name: 'Promotions',
        path: '/admin/promotions',
        icon: <Store size={collapsed ? 22 : 18} />,
      },
      {
        name: 'Laboratoires',
        path: '/admin/laboratories',
        icon: <Building2 size={collapsed ? 22 : 18} />
      },
      {
        name: 'Transactions',
        path: '/admin/transactions',
        icon: <BarChart2 size={collapsed ? 22 : 18} />
      },
      {
        name: 'Demande de paiement',
        path: '/admin/payment-requests',
        icon: <CreditCard size={collapsed ? 22 : 18} />,
        unreadCount: pendingPaymentCount
      },
      {
        name: 'Messages',
        path: '/admin/messages',
        icon: <MessageSquare size={collapsed ? 22 : 18} />,
        unreadCount: unreadMessageCount
      },
      {
        name: 'Notifications', // Added notifications for admin
        path: '/admin/notifications',
        icon: <Bell size={collapsed ? 22 : 18} />,
        unreadCount: unreadNotificationCount
      },
      {
        name: 'Fraudes',
        path: '/admin/fraud-cases',
        icon: <ShieldAlert size={collapsed ? 22 : 18} />,
        unreadCount: unreadFraudCount
      },
      {
        name: 'Paramètres',
        path: '/admin/settings',
        icon: <Settings size={collapsed ? 22 : 18} />
      },
      {
        name: 'Simulateur financier',
        path: '/admin/simulator',
        icon: <TrendingUp size={collapsed ? 22 : 18} />
      },
      {
        name: 'Parrainage',
        path: '/admin/referral',
        icon: <GitBranch size={collapsed ? 22 : 18} />
      },
      {
        name: 'Publicités',
        path: '/admin/advertising',
        icon: <Megaphone size={collapsed ? 22 : 18} />,
      },
      {
        name: 'Créer une promotion',
        path: '/admin/create-promotion',
        icon: <Plus size={collapsed ? 22 : 18} />,
        accent: true
      },
    ],
    LABORATORY: [
      {
        name: 'Dashboard',
        path: '/laboratory/dashboard',
        icon: <LayoutGrid size={collapsed ? 22 : 18} />
      },
      {
        name: 'Produits',
        path: '/laboratory/products',
        icon: <Package size={collapsed ? 22 : 18} />
      },
      {
        name: 'Promotions',
        path: '/laboratory/promotions',
        icon: <Tag size={collapsed ? 22 : 18} />,
      },
      {
        name: 'Transactions',
        path: '/laboratory/transactions',
        icon: <BarChart2 size={collapsed ? 22 : 18} />
      },
      {
        name: 'Contrats',
        path: '/laboratory/contracts',
        icon: <FileText size={collapsed ? 22 : 18} />
      },
      {
        name: 'Messages',
        path: '/laboratory/messages',
        icon: <MessageSquare size={collapsed ? 22 : 18} />,
        unreadCount: labUnreadMessageCount,
        onClick: handleLabMessagesClick,
      },
      {
        name: 'Notifications', // Added notifications for laboratory
        path: '/laboratory/notifications',
        icon: <Bell size={collapsed ? 22 : 18} />,
        unreadCount: unreadNotificationCount
      },
    ]
  };
  
  // Get navigation items based on user type
  const navItems = navigationItems[userType] || navigationItems.ADMIN;
  
  // Toggle menu item expansion
  const toggleExpand = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };
  
  // Check if a menu item or its children should be highlighted
  const isActive = (item) => {
    if (item.path) {
      if (location.pathname === item.path) return true;
      // Highlight parent when on a child route (e.g., /admin/laboratories/:id)
      if (location.pathname.startsWith(item.path + '/')) return true;
    }

    if (item.children) {
      return item.children.some(child => location.pathname === child.path);
    }

    return false;
  };
  
  // Handle sidebar collapse toggle
  const toggleSidebar = () => {
    setCollapsed(prev => !prev);
  };

  // Close sidebar on mobile after navigating
  const handleNavClick = (item) => {
    if (isMobileWidth() && !collapsed) {
      setCollapsed(true);
    }
    if (item.onClick) item.onClick();
  };
  
  // Handle logout
  const handleLogout = () => {
    authApi.logout();
    navigate('/login');
  };
  
  const isMobileNow = isMobileWidth();

  return (
    <>
      {/* Mobile backdrop overlay */}
      {isMobileNow && !collapsed && (
        <div
          className="fixed inset-0 bg-black/50 z-20"
          onClick={toggleSidebar}
        />
      )}
    <div
      className={`
        fixed top-0 left-0 h-full ${c.bg} text-white transition-all duration-300 z-30
        ${isMobileNow
          ? collapsed ? '-translate-x-full w-64' : 'translate-x-0 w-64'
          : collapsed ? 'w-16' : 'w-64'
        }
      `}
    >
      {/* Logo and title - Fixed logo display */}
      <div className={`py-5 flex justify-center items-center border-b ${c.border}`}>
        {collapsed ? (
          <img 
            src={logoM} 
            alt="Logo M" 
            className="h-8 w-8 object-contain"
            onError={(e) => {
              // Fallback if logo image fails to load
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'block';
            }}
          />
        ) : (
          <img 
            src={logo} 
            alt="Logo" 
            className="h-10 object-contain max-w-[200px]"
            onError={(e) => {
              // Fallback if logo image fails to load
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'block';
            }}
          />
        )}
        {/* Fallback text if images don't load */}
        <div className="hidden">
          {collapsed ? (
            <div className="text-2xl font-bold">M</div>
          ) : (
            <div className="text-xl font-bold">MAZID</div>
          )}
        </div>
      </div>
      
      {/* Toggle sidebar button — desktop only */}
      {!isMobileNow && (
        <button
          onClick={toggleSidebar}
          className={`absolute -right-3 top-16 bg-white ${c.toggleText} rounded-full p-1 shadow-md border ${c.toggleBorder} hover:bg-gray-50 transition-colors`}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      )}
      
      {/* User info */}
      <div className={`px-3 py-3 border-b ${c.border} ${collapsed ? 'text-center' : ''}`}>
        {collapsed ? (
          <Link to="/profile" title="Changer mon mot de passe">
            <div className={`inline-flex h-10 w-10 rounded-full ${c.avatar} text-white items-center justify-center font-semibold ${c.hoverAvatar} transition-colors`}>
              {userData.firstName ? userData.firstName[0] : 'U'}
            </div>
          </Link>
        ) : (
          <Link to="/profile" className="flex items-center space-x-3 group">
            <div className={`h-10 w-10 rounded-full ${c.avatar} text-white flex items-center justify-center font-semibold ${c.hoverAvatar} transition-colors`}>
              {userData.firstName ? userData.firstName[0] : 'U'}
            </div>
            <div className="flex-1 truncate">
              <p className="text-sm font-medium truncate">
                {userData.firstName} {userData.lastName}
              </p>
              <p className={`text-xs ${c.subtext} truncate flex items-center gap-1`}>
                <UserCircle size={11} />
                Changer mon mot de passe
              </p>
            </div>
          </Link>
        )}
      </div>
      
      {/* Navigation links */}
      <div className="py-2 overflow-y-auto" style={{ height: 'calc(100% - 180px)' }}>
        <nav>
          <ul>
            {navItems.map((item) => (
              <li key={item.name} className="mb-1 px-2">
                {!item.children ? (
                  <Link
                    to={item.path}
                    onClick={() => handleNavClick(item)}
                    className={`
                      flex items-center px-2 py-2 rounded-md transition-colors
                      ${item.accent
                        ? isActive(item)
                          ? 'bg-blue-600 text-white'
                          : 'bg-blue-500/20 text-blue-200 hover:bg-blue-500 hover:text-white'
                        : isActive(item)
                          ? `${c.activeItem} text-white`
                          : `${c.text} ${c.hoverItem} hover:text-white`}
                      ${collapsed ? 'justify-center' : 'justify-between'}
                    `}
                  >
                    <span className="flex items-center">
                      <span className={`${collapsed ? '' : 'mr-3'} relative`}>
                        {item.icon}
                        {/* Show unread count badge for notifications */}
                        {item.unreadCount > 0 && (
                          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]">
                            {item.unreadCount > 99 ? '99+' : item.unreadCount}
                          </span>
                        )}
                      </span>
                      {!collapsed && (
                        <span className="flex items-center justify-between w-full">
                          <span>{item.name}</span>
                        </span>
                      )}
                    </span>
                  </Link>
                ) : (
                  <div>
                    <button
                      onClick={() => toggleExpand(item.name)}
                      className={`
                        w-full flex items-center px-2 py-2 rounded-md transition-colors
                        ${isActive(item)
                          ? `${c.activeItem} text-white`
                          : `${c.text} ${c.hoverItem} hover:text-white`}
                        ${collapsed ? 'justify-center' : 'justify-between'}
                      `}
                    >
                      <span className="flex items-center">
                        <span className={`${collapsed ? '' : 'mr-3'}`}>{item.icon}</span>
                        {!collapsed && <span>{item.name}</span>}
                      </span>
                      {!collapsed && (
                        <ChevronDown 
                          size={16} 
                          className={`transform transition-transform ${expandedGroups[item.name] ? 'rotate-180' : ''}`} 
                        />
                      )}
                    </button>
                    
                    {/* Dropdown items */}
                    {(expandedGroups[item.name] || collapsed) && (
                      <div 
                        className={`
                          transition-all duration-200 overflow-hidden
                          ${collapsed
                            ? `absolute left-16 top-0 mt-0 ml-1 z-50 ${c.bg} rounded-md shadow-lg p-2 min-w-[160px]`
                            : 'ml-6 mt-1'}
                        `}
                        style={{ 
                          display: collapsed && !expandedGroups[item.name] ? 'none' : 'block',
                          // Dynamically position dropdown in collapsed mode
                          ...(collapsed ? { top: `${navItems.indexOf(item) * 40 + 120}px` } : {})
                        }}
                        onMouseEnter={() => collapsed && toggleExpand(item.name)}
                        onMouseLeave={() => collapsed && toggleExpand(item.name)}
                      >
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.path}
                            onClick={() => handleNavClick(child)}
                            className={`
                              block px-3 py-2 rounded-md text-sm transition-colors
                              ${location.pathname === child.path
                                ? `${c.avatar} text-white`
                                : `${c.text} ${c.hoverItem} hover:text-white`}
                            `}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>
      
      {/* Bottom buttons */}
      <div className={`absolute bottom-0 left-0 w-full p-3 border-t ${c.border} space-y-1`}>
        {isPreviewMode && (
          <button
            onClick={handleExitPreview}
            className={`
              w-full flex items-center px-2 py-2 rounded-md ${c.text}
              hover:bg-amber-500 hover:text-white transition-colors
              ${collapsed ? 'justify-center' : ''}
            `}
            title="Quitter la vue labo"
          >
            <LogOut size={collapsed ? 22 : 18} className={`${collapsed ? '' : 'mr-3'} rotate-180`} />
            {!collapsed && <span>Quitter la vue labo</span>}
          </button>
        )}
        <button
          onClick={handleLogout}
          className={`
            w-full flex items-center px-2 py-2 rounded-md ${c.text}
            hover:bg-red-600 hover:text-white transition-colors
            ${collapsed ? 'justify-center' : ''}
          `}
        >
          <LogOut size={collapsed ? 22 : 18} className={`${collapsed ? '' : 'mr-3'}`} />
          {!collapsed && <span>Déconnexion</span>}
        </button>
      </div>
    </div>
    </>
  );
};

export default Sidebar;